import { until } from "@vueuse/core";

import { storeToRefs } from "pinia";
import { watch } from "vue";

import { useBusinessEntitiesStore } from "@/stores/business-entities-store";

import { BusinessEntityEntity } from ".";
import { BaseLinkedEntitiesManager } from "../mystekz-linked-entities/manager";

/**
 * Manages the business entities and replaces
 */
export class BusinessEntitiesManager extends BaseLinkedEntitiesManager<BusinessEntityEntity> {
    nodeType = "business_entity" as const;

    constructor(baseUrl: string) {
        const { entities, isLoading } = storeToRefs(useBusinessEntitiesStore());

        super(baseUrl);
        this.events = new EventTarget();
        this.ready = until(isLoading).not.toBeTruthy();

        watch(
            entities,
            async (newValue) => {
                if (newValue) {
                    this.setEntities(newValue);
                }
            },
            { immediate: true, deep: true }
        );
    }

    get _hrefFormatLegacy() {
        return /stekz-entity:(?<id>[A-Za-z0-9_-]+)/;
    }

    get _textFormatLegacy() {
        return /domain-object:(?<name>.*)/;
    }

    check(href: string, text: string) {
        // Check for new format using base class implementation
        const baseMatch = super.check(href, text);
        if (baseMatch) {
            return baseMatch;
        }

        // Check for old format for backwards compatibility
        const match = this._hrefFormatLegacy.exec(href);
        if (match && this._textFormatLegacy.exec(text)) {
            return match;
        } else {
            return false;
        }
    }

    extract(href: string, text: string) {
        // Try new format first using base class implementation
        try {
            return super.extract(href, text);
        } catch {
            // Fall back to old format for backwards compatibility
            const id = this._hrefFormatLegacy.exec(href)?.groups?.["id"];
            const name = this._textFormatLegacy.exec(text)?.groups?.["name"];
            return (
                this.findById(id || "") ||
                ({
                    type: "business_entity",
                    state: "pending",
                    name: name,
                    id: id,
                    href: href,
                } as BusinessEntityEntity)
            );
        }
    }
}
